import {
  AtomIcon,
  GlobeIcon,
  GraduationCapIcon,
  OrbitIcon,
  RocketIcon,
  SparklesIcon,
  StarIcon,
  SunMoonIcon,
  TelescopeIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  description: string;
  result: string;
  note: string;
  icon: LucideIcon;
  tags: string;
};

export const modules: Module[] = [
  {
    number: '01',
    title: 'Fundamentos de Astronomía para la Enseñanza',
    subtitle: 'Una introducción completa y didáctica para enseñar Astronomía desde sus bases',
    description:
      'Qué es la Astronomía, historia y evolución, ramas principales, escalas y distancias astronómicas, unidades utilizadas, movimientos y fenómenos celestes, orientación y reconocimiento del cielo. Todo el contexto que necesitás para hablar con autoridad desde el primer día.',
    result:
      'Tus alumnos entienden por qué la Astronomía es una ciencia fundamental, qué preguntas hace la astronomía moderna, cuál es la importancia de observar el cielo desde diferentes lugares del planeta, y por qué la astronomía ha transformado nuestra comprensión del universo.',
    note: 'Contenido completo con recursos didácticos',
    icon: TelescopeIcon,
    tags: 'QUÉ ES LA ASTRONOMÍA · HISTORIA Y EVOLUCIÓN · RAMAS · ESCALAS Y DISTANCIAS · MOVIMIENTOS CELESTES · ORIENTACIÓN EN EL CIELO',
  },
  {
    number: '02',
    title: 'El Sistema Solar Completo',
    subtitle: 'Un recorrido completo por nuestro sistema planetario y sus principales componentes',
    description:
      'Formación y estructura del Sistema Solar, el Sol como estrella central, planetas rocosos y gigantes, planetas enanos, satélites naturales, asteroides, cometas y meteoroides, cinturón de asteroides, movimientos y órbitas, comparaciones entre planetas. Con recursos didácticos para enseñar.',
    result:
      'Tus alumnos comprenden la estructura del Sistema Solar, pueden comparar características planetarias, entienden la mecánica orbital y conocen los diferentes cuerpos que habitan nuestro sistema. Saben por qué Plutón es un planeta enano y qué significa eso científicamente.',
    note: 'Contenido completo con comparativas y modelos',
    icon: OrbitIcon,
    tags: 'FORMACIÓN DEL SISTEMA SOLAR · EL SOL · PLANETAS ROCOSOS Y GIGANTES · PLANETAS ENANOS · SATÉLITES · ASTEROIDES Y COMETAS · ÓRBITAS',
  },
  {
    number: '03',
    title: 'Tierra, Sol y Luna',
    subtitle: 'Los fenómenos astronómicos que afectan directamente a nuestro planeta, con especial atención al hemisferio sur',
    description:
      'La Tierra como planeta, rotación y traslación, inclinación del eje terrestre, día y noche, estaciones del año en Argentina, solsticios y equinoccios, la Luna y sus características, fases lunares, eclipses solares y lunares, mareas, relación Tierra-Sol-Luna y observación segura de fenómenos solares.',
    result:
      'Tus alumnos entienden por qué hay estaciones en Argentina, cómo funcionan los eclipses, por qué la Luna tiene fases diferentes, cómo las mareas están relacionadas con la Luna, y cómo observar fenómenos solares de forma segura. Comprenden su propio planeta desde una perspectiva cósmica.',
    note: 'Contenido específico para el hemisferio sur',
    icon: SunMoonIcon,
    tags: 'ROTACIÓN Y TRASLACIÓN · ESTACIONES EN ARGENTINA · SOLSTICIOS Y EQUINOCCIOS · FASES LUNARES · ECLIPSES · MAREAS · OBSERVACIÓN SOLAR SEGURA',
  },
  {
    number: '04',
    title: 'Estrellas, Constelaciones y Observación del Cielo',
    subtitle: 'Una guía para comprender las estrellas y aprender a interpretar el cielo observable',
    description:
      'Qué son las estrellas, características y clasificación, color, temperatura y luminosidad, nacimiento, evolución y muerte estelar, constelaciones, cielo austral, reconocimiento de patrones celestes, mapas y cartas del cielo, orientación nocturna, observación a simple vista y con instrumentos, actividades de observación para el aula.',
    result:
      'Tus alumnos pueden reconocer constelaciones desde Argentina, entender el ciclo de vida de las estrellas, interpretar un mapa del cielo, orientarse usando las estrellas y diseñar actividades de observación seguras. Saben qué están mirando cuando observan el cielo nocturno.',
    note: 'Mapas estelares del cielo austral incluidos',
    icon: StarIcon,
    tags: 'CLASIFICACIÓN ESTELAR · EVOLUCIÓN Y MUERTE ESTELAR · CONSTELACIONES · CIELO AUSTRAL · CARTAS DEL CIELO · ORIENTACIÓN NOCTURNA',
  },
  {
    number: '05',
    title: 'Galaxias, Vía Láctea y Universo Profundo',
    subtitle: 'Una exploración desde nuestra galaxia hasta algunas de las estructuras más grandes del universo',
    description:
      'Qué son las galaxias, tipos y clasificación, estructura de la Vía Láctea, ubicación del Sistema Solar, cúmulos estelares, nebulosas, galaxias cercanas, grupos y cúmulos de galaxias, universo profundo, observación y representación de objetos lejanos.',
    result:
      'Tus alumnos entienden dónde estamos en el universo, cómo se estructura la Vía Láctea, qué son las nebulosas y cúmulos, cómo se ve el universo profundo a través de diferentes telescopios y comprenden la escala monumental del cosmos.',
    note: 'Contenido completo con visualizaciones',
    icon: SparklesIcon,
    tags: 'TIPOS DE GALAXIAS · ESTRUCTURA DE LA VÍA LÁCTEA · CÚMULOS ESTELARES · NEBULOSAS · GRUPOS Y CÚMULOS · UNIVERSO PROFUNDO',
  },
  {
    number: '06',
    title: 'Cosmología, Origen y Evolución del Universo',
    subtitle: 'Los grandes interrogantes sobre el origen, estructura y evolución del cosmos explicados de forma didáctica',
    description:
      'Introducción a la Cosmología, universo observable, modelo del Big Bang, expansión del universo, edad aproximada del universo (13.800 millones de años), radiación cósmica de fondo, formación de las primeras estructuras, materia oscura, energía oscura, evolución del universo y grandes preguntas de la Cosmología moderna.',
    result:
      'Tus alumnos comprenden por qué los cosmólogos sostienen el modelo del Big Bang, qué evidencias lo sustentan, qué son la materia y la energía oscura, por qué el universo se expande, y conocen los grandes misterios que aún intrigan a la ciencia actual. Pueden defender científicamente sus respuestas.',
    note: 'Contenido actualizado con hallazgos recientes',
    icon: AtomIcon,
    tags: 'UNIVERSO OBSERVABLE · BIG BANG · EXPANSIÓN · RADIACIÓN CÓSMICA DE FONDO · MATERIA OSCURA · ENERGÍA OSCURA · PREGUNTAS ABIERTAS',
  },
  {
    number: '07',
    title: 'Exoplanetas y Búsqueda de Vida en el Universo',
    subtitle: 'Una introducción actual a uno de los campos más fascinantes de la Astronomía',
    description:
      'Qué son los exoplanetas, sistemas planetarios extrasolares, métodos de detección (tránsito, velocidad radial), tipos de exoplanetas, zona habitable, atmósferas planetarias, condiciones necesarias para la vida, astrobiología, búsqueda de biofirmas y preguntas abiertas sobre vida extraterrestre.',
    result:
      'Tus alumnos conocen los exoplanetas descubiertos, entienden cómo se detectan planetas orbitando otras estrellas, comprenden qué es la zona habitable, saben por qué los científicos buscan vida extraterrestre, y pueden debatir sobre la posibilidad de vida en otros mundos con argumentos científicos.',
    note: 'Datos actualizados de misiones de detección',
    icon: GlobeIcon,
    tags: 'MÉTODOS DE DETECCIÓN · TRÁNSITO Y VELOCIDAD RADIAL · TIPOS DE EXOPLANETAS · ZONA HABITABLE · ASTROBIOLOGÍA · BIOFIRMAS',
  },
  {
    number: '08',
    title: 'Exploración Espacial, Misiones y Tecnología Astronómica',
    subtitle: 'La Astronomía desde la perspectiva de la tecnología y la exploración espacial',
    description:
      'Historia de la exploración espacial, carrera espacial, satélites artificiales, sondas espaciales, misiones tripuladas y no tripuladas, exploración de la Luna, exploración de Marte y otros mundos, telescopios terrestres y espaciales, observación en diferentes regiones del espectro electromagnético, tecnología para estudiar el universo, presente y futuro de la exploración espacial.',
    result:
      'Tus alumnos conocen los hitos principales de la exploración espacial, entienden cómo funcionan los telescopios, saben qué tecnología se usa para observar el universo, comprenden las misiones actuales a Marte y otros mundos, y pueden debatir sobre el futuro de la exploración espacial.',
    note: 'Contenido actualizado con misiones 2025-2026',
    icon: RocketIcon,
    tags: 'CARRERA ESPACIAL · SATÉLITES Y SONDAS · MISIONES TRIPULADAS · LUNA Y MARTE · TELESCOPIOS TERRESTRES Y ESPACIALES · ESPECTRO ELECTROMAGNÉTICO',
  },
  {
    number: '09',
    title: 'Astronomía en el Aula',
    subtitle: 'El tomo específicamente diseñado para transformar los contenidos astronómicos en experiencias educativas',
    description:
      'Cómo enseñar Astronomía, estrategias didácticas, ideas para explicar conceptos complejos, modelos y representaciones, experiencias prácticas, observación astronómica escolar, uso seguro de recursos para observación solar, trabajo interdisciplinario, aprendizaje basado en proyectos, evaluación de aprendizajes y recursos para despertar curiosidad científica.',
    result:
      'Tus alumnos participan en observaciones reales, construyen modelos que comprenden, hacen preguntas genuinas, relacionan la Astronomía con otras disciplinas y desarrollan curiosidad científica sostenida. La clase se convierte en un espacio de asombro y descubrimiento.',
    note: '150 actividades listas para implementar',
    icon: GraduationCapIcon,
    tags: 'ESTRATEGIAS DIDÁCTICAS · MODELOS Y REPRESENTACIONES · OBSERVACIÓN ESCOLAR · OBSERVACIÓN SOLAR SEGURA · ABP · EVALUACIÓN DE APRENDIZAJES',
  },
];
